"use client";

import { motion } from "motion/react";

import { trackCatalogEvent } from "@/components/catalog/analytics";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { usePressedInteraction } from "@/components/ui/motion-primitives";
import { homeContent } from "@/data/home-content";

export const whatsappMorphLayoutId = "whatsapp-morph-action";

const floatingButtonClassName =
  "fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-2 z-30 inline-flex h-12 w-12 items-center justify-center rounded-full shadow-md outline-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2";

type FloatingWhatsAppButtonProps =
  | {
      readonly disabled?: never;
      readonly href: string;
      readonly isBusy?: never;
      readonly onClick?: never;
      readonly type?: never;
    }
  | {
      readonly href?: never;
      readonly disabled?: boolean;
      readonly isBusy?: boolean;
      readonly onClick: () => void;
      readonly type: "button";
    };

export function FloatingWhatsAppButton(props: Readonly<FloatingWhatsAppButtonProps>) {
  const interaction = usePressedInteraction();

  if (props.href) {
    return (
      <motion.a
        animate={{ opacity: 1, scale: 1 }}
        aria-label={homeContent.whatsappLabel}
        className={floatingButtonClassName}
        exit={{ opacity: 0, scale: 0.92 }}
        href={props.href}
        initial={{ opacity: 0, scale: 0.92 }}
        layoutId={whatsappMorphLayoutId}
        onClick={() => {
          trackCatalogEvent(
            {
              eventName: "whatsapp_product_consult_click",
              payload: { source: "floating_button" },
            },
            { keepalive: true },
          );
        }}
        rel="noreferrer"
        {...interaction}
      >
        <WhatsAppIcon className="h-12 w-12" variant="color" />
      </motion.a>
    );
  }

  return (
    <motion.button
      animate={{ opacity: 1, scale: 1 }}
      aria-busy={props.isBusy}
      aria-label={homeContent.whatsappLabel}
      className={floatingButtonClassName}
      disabled={props.disabled || props.isBusy}
      exit={{ opacity: 0, scale: 0.92 }}
      initial={{ opacity: 0, scale: 0.92 }}
      layoutId={whatsappMorphLayoutId}
      onClick={props.onClick}
      type="button"
      {...interaction}
    >
      <WhatsAppIcon className="h-12 w-12" variant="color" />
    </motion.button>
  );
}
